import { useState, useEffect } from 'react';
import styled from 'styled-components';
import Banner from '../components/Banner';
import Tournament from '../services/Tournament';
import FunctionalSkills from '../components/FunctionalSkills';
import Multiplayer from '../components/Multiplayer';
import PlayTogether from '../components/PlayTogether';
import MyZone from '../components/MyZone';
import CboardButton from '../components/CboardButton';
import { getFunctionalSkills } from '../../modules/functional_skills_api';
import { getActiveTournament } from '../../modules/tournament_api';
import { getDashboardBanner, loadImageFromUrl } from '../../modules/communication';
import SplitIOTreatment from '../../components/splitio/splitioTreatment';
import ChristmasEasterEgg from '../../components/containers/play-screen/easter-eggs/christmas/christmas-easter-egg';
import HanukkahEasterEgg from '../../components/containers/play-screen/easter-eggs/hanukkah/hanukkah-easter-egg.jsx';
import EasterEggDrawer from '../components/EasterEggDrawer/EasterEggDrawer';
import WelcomePopup from '../components/WelcomePopup';

const Grid = styled.div`
  display: grid;
  grid-gap: 2rem;
  grid-template-columns: 1fr;
  grid-template-areas:
    'main1'
    'main2'
    'main3'
    'sidebar'
    'fullwidth';

  @media screen and (min-width: 768px) {
    grid-template-columns: calc(75% - 2rem) 25%;
    grid-template-areas:
      'main1 sidebar'
      'main2 sidebar'
      'main3 cboard'
      'fullwidth fullwidth';
  }
`;

const MainCell = styled.div`
  grid-area: main1;
`;

const SidebarCell = styled.div`
  grid-area: sidebar;
`;

const BannerCell = styled.div`
  grid-area: main2;
`;

const TogetherCell = styled.div`
  grid-area: main3;
`;

const CboardButtonCell = styled.div`
  grid-area: cboard;
  padding-top: 3.3rem;
`;

const CarouselCell = styled.div`
  grid-area: fullwidth;
`;

const Dashboard = () => {
  const [functionalSkills, setFunctionalSkills] = useState([]);
  const [banner, setBanner] = useState(false);

  const getFunctionalSkillData = async () => {
    const functionalSkillsResponse = await getFunctionalSkills();
    if (functionalSkillsResponse.data && functionalSkillsResponse.data.success) {
      return functionalSkillsResponse.data.functional_skills;
    } else {
      return [];
    }
  };

  useEffect(() => {
    getFunctionalSkillData().then(setFunctionalSkills);
  }, [setFunctionalSkills]);

  useEffect(() => {
    getActiveTournament()
      .then((tournamentResults) => {
        if (tournamentResults.data.success) {
          const { active } = tournamentResults.data;
          const { name, bannerImage } = tournamentResults.data.tournament;

          return {
            tournament: tournamentResults.data.tournament,
            active,
            banner: {
              children: `${active ? 'Live Now:' : 'Recently Ended:'} ${name} (Click for more info)`,
              bannerImage: bannerImage ? `TOURNAMENT_BANNER/${bannerImage}` : false,
              clickCallback: () => Tournament.showTournamentDialog('/play/tournament/'),
            },
          };
        }
        return { tournament: false };
      })
      .then((data) => {
        if (!data.tournament) {
          return getDashboardBanner().then((bannerResults) => {
            if (bannerResults.data.success) {
              const { description, link_url, image_filename } = bannerResults.data.dashboardBanner;

              return {
                tournament: false,
                active: data.active,
                banner: {
                  children: description,
                  link: link_url,
                  bannerImage: image_filename,
                },
              };
            } else {
              return {
                tournament: false,
                active: data.active,
                banner: {
                  children: 'Welcome to RESTORESKILLS',
                },
              };
            }
          });
        } else {
          return data;
        }
      })
      .then((data) => {
        const { children, link, clickCallback } = data.banner;
        const tournament = data.tournament;

        if (tournament) {
          tournament.active = data.active;
        }

        if (data.banner.bannerImage) {
          loadImageFromUrl(data.banner.bannerImage).then((bannerImage) => {
            setBanner({
              children,
              link,
              clickCallback,
              bannerImage,
            });
          });
        } else {
          setBanner({
            children,
            link,
            clickCallback,
          });
        }
      });
  }, [setBanner]);

  return (
    <Grid>
      <SplitIOTreatment featureName="welcome_popup" treatmentValue="on">
        {(treatment) => {
          if (treatment === 'on') {
            return <WelcomePopup />;
          } else {
            return null;
          }
        }}
      </SplitIOTreatment>
      <MainCell>
        <FunctionalSkills skills={functionalSkills} />
      </MainCell>

      {banner && (
        <BannerCell>
          <Banner {...banner} />
        </BannerCell>
      )}

      <TogetherCell>
        <PlayTogether />
      </TogetherCell>

      <SplitIOTreatment featureName={'DC-1765-cboard-integration'} treatmentValue="on" trafficType="business-group">
        {(treatment) => {
          if (treatment === 'on') {
            return (
              <CboardButtonCell>
                <CboardButton />
              </CboardButtonCell>
            );
          } else {
            return null;
          }
        }}
      </SplitIOTreatment>

      <CarouselCell>
        <Multiplayer />
      </CarouselCell>

      <SidebarCell>
        <MyZone></MyZone>
      </SidebarCell>

      <SplitIOTreatment featureName={'holiday-easter-eggs'} treatmentValue="on" trafficType="org">
        {(treatment) => {
          if (treatment === 'on') {
            return (
              <EasterEggDrawer>
                <ChristmasEasterEgg />
                <HanukkahEasterEgg />
              </EasterEggDrawer>
            );
          } else {
            return null;
          }
        }}
      </SplitIOTreatment>
    </Grid>
  );
};

export default Dashboard;
